import { useCallback } from 'react';

import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { useAllOrgUnits } from '~/data/orgunits';
import { OrganizationUnit } from '~/data/orgunits/types';

export type EmployeeItemInfoData = {
  employeeId: number;
  employee?: Employees;
  employeeUnit?: OrganizationUnit;
};

type UseEmployeeItemInfoOutput = {
  getEmployeeItemInfoById: (employeeIds: number) => EmployeeItemInfoData;
  getEmployeeItemInfoByIds: (employeeIds: number[]) => EmployeeItemInfoData[];
  unknownEmployeeInfo: EmployeeItemInfoData;
};

export const useEmployeeItemInfo = (): UseEmployeeItemInfoOutput => {
  const { allEmployees } = useAllEmployees();
  const { orgUnitsIncludeDeleted } = useAllOrgUnits();

  const unknownEmployeeInfo: EmployeeItemInfoData = { employeeId: 0 };

  const findEmployee = useCallback(
    (employeeId: number): Employees | undefined => {
      return Object.values(allEmployees).find(
        (emp) => emp.fEmpId === employeeId,
      );
    },
    [allEmployees],
  );

  const findOrgUnit = useCallback(
    (unitId: number): OrganizationUnit | undefined => {
      return Object.values(orgUnitsIncludeDeleted).find(
        (orgUnit) => orgUnit.fOrgUnitId === unitId,
      );
    },
    [orgUnitsIncludeDeleted],
  );

  const getEmployeeItemInfoById = useCallback(
    (employeeId: number): EmployeeItemInfoData => {
      const employee = findEmployee(employeeId);

      return {
        employeeId,
        employee,
        employeeUnit: !!employee?.fEmpUnit
          ? findOrgUnit(employee?.fEmpUnit)
          : undefined,
      };
    },
    [findEmployee, findOrgUnit],
  );

  const getEmployeeItemInfoByIds = useCallback(
    (employeeIds: number[]): EmployeeItemInfoData[] => {
      return employeeIds.map(getEmployeeItemInfoById);
    },
    [getEmployeeItemInfoById],
  );

  return {
    getEmployeeItemInfoById,
    getEmployeeItemInfoByIds,
    unknownEmployeeInfo,
  };
};
